import { formatMoney } from "@/core/utils/money";
import {
  PAYROLL_ROSTER_FIELD_FORMAT,
  PAYROLL_ROSTER_FIELD_TYPE,
  type PayrollRosterField,
  type PayrollRosterMember,
  type PayrollRosterValue,
} from "@/payroll/roster/types";
import { rosterTeamNames, sameTeamIds } from "@/payroll/teams/utils";
import { isBlankRosterValue } from "./members";

// Formats a stored value for read-only display. Numbers honor the field's
// UI-only `format`: currency → "$1,234.56" (via the shared money util), percent
// → "5.5%", plain/absent → the number as-is. Non-number fields (and callers
// without a field) are unaffected: true/false → Yes/No, blank → "-", else raw.
// Scripts store fixed-option values as snake_case tokens (`service_tech`), which
// is the right shape for code and the wrong one to show a person. Only tokens
// that are entirely lower snake_case are rewritten, so values a business already
// wrote for humans ("HVAC Service Tech 5%", "PLUMBING") pass through untouched
// and acronyms are never mangled. Display only — the stored value never changes.
const SNAKE_CASE_TOKEN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

export const humanizeRosterOption = (value: string): string => {
  if (!SNAKE_CASE_TOKEN.test(value)) return value;

  const spaced = value.replaceAll("_", " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const formatRosterValue = (
  value: PayrollRosterValue | undefined,
  field?: PayrollRosterField,
) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (isBlankRosterValue(value)) return "-";

  if (field?.field_type === PAYROLL_ROSTER_FIELD_TYPE.number) {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      if (field.format === PAYROLL_ROSTER_FIELD_FORMAT.currency) {
        return formatMoney(numeric) ?? String(value);
      }
      if (field.format === PAYROLL_ROSTER_FIELD_FORMAT.percent) {
        return `${numeric}%`;
      }
    }
  }

  return field?.field_type === PAYROLL_ROSTER_FIELD_TYPE.fixedOption
    ? humanizeRosterOption(String(value))
    : String(value);
};

// Strips everything except digits and a single decimal point, so number inputs
// only ever hold a clean plain number (currency/percent are display-only).
export const maskNumericValue = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  return (
    cleaned.slice(0, firstDot + 1) +
    cleaned.slice(firstDot + 1).replace(/\./g, "")
  );
};

const sameRosterValue = (
  first: PayrollRosterValue | undefined,
  second: PayrollRosterValue | undefined,
  field?: PayrollRosterField,
) => {
  if (isBlankRosterValue(first) && isBlankRosterValue(second)) return true;
  if (first === second) return true;

  if (field?.field_type === PAYROLL_ROSTER_FIELD_TYPE.number) {
    const firstNumber = Number(first);
    const secondNumber = Number(second);
    return (
      !Number.isNaN(firstNumber) &&
      !Number.isNaN(secondNumber) &&
      firstNumber === secondNumber
    );
  }

  return false;
};

export interface RosterFieldChange {
  key: string;
  label: string;
  field?: PayrollRosterField;
  before: PayrollRosterValue | undefined;
  after: PayrollRosterValue | undefined;
}

export interface MemberChange {
  id: string;
  kind: "added" | "updated" | "removed" | "dismissed";
  name: string;
  employeeId: string;
  fields: RosterFieldChange[];
}

interface DiffDraft {
  member: PayrollRosterMember;
  isNew: boolean;
}

// Indexes the baseline members by their ServiceTitan employee id (the natural
// key). Members can now be added AND removed, so positional mapping no longer
// holds — everything matches on ST id.
const indexByStId = (members: readonly PayrollRosterMember[]) => {
  const map = new Map<string, PayrollRosterMember>();
  members.forEach((member) => {
    if (member.service_titan_employee_id) {
      map.set(member.service_titan_employee_id, member);
    }
  });
  return map;
};

// Diffs the edited drafts against the published baseline, matching by ST id:
// working members with no baseline match are additions, changed matches are
// updates, and baseline members absent from the working copy are removals.
export const diffRoster = (
  drafts: DiffDraft[],
  baseline: PayrollRosterMember[],
  fields: PayrollRosterField[],
  // Suggestions the owner said do not belong on payroll. They were never
  // members, so they cannot be diffed out of the baseline — they are only
  // visible as the difference between the two dismissal lists.
  dismissals: { added: string[]; names: Map<string, string> } = {
    added: [],
    names: new Map(),
  },
  userNames: ReadonlyMap<string, string> = new Map(),
): MemberChange[] => {
  const changes: MemberChange[] = [];
  const baselineByStId = indexByStId(baseline);
  const workingStIds = new Set<string>();

  drafts.forEach((draft, draftIndex) => {
    const { member, isNew } = draft;
    const employeeId = member.service_titan_employee_id;
    const identity = employeeId || `row-${draftIndex}`;
    if (employeeId) workingStIds.add(employeeId);

    const base = isNew ? undefined : baselineByStId.get(employeeId);

    if (!base) {
      const fieldChanges: RosterFieldChange[] = [];
      fields.forEach((field) => {
        const after = member.field_values[field.key];
        if (!isBlankRosterValue(after)) {
          fieldChanges.push({
            key: field.key,
            label: field.label,
            field,
            before: undefined,
            after,
          });
        }
      });
      changes.push({
        id: `added-${identity}`,
        kind: "added",
        name: member.display_name,
        employeeId,
        fields: fieldChanges,
      });
      return;
    }

    const fieldChanges: RosterFieldChange[] = [];
    if (member.display_name !== base.display_name) {
      fieldChanges.push({
        key: "__name",
        label: "Name",
        before: base.display_name,
        after: member.display_name,
      });
    }
    if ((member.user_id ?? null) !== (base.user_id ?? null)) {
      fieldChanges.push({
        key: "__user",
        label: "Employee profile",
        before: base.user_id
          ? (userNames.get(base.user_id) ?? "Unavailable employee")
          : "Not set",
        after: member.user_id
          ? (userNames.get(member.user_id) ?? "Unavailable employee")
          : "Not set",
      });
    }
    if (!sameTeamIds(member.team_access_key_ids, base.team_access_key_ids)) {
      fieldChanges.push({
        key: "__teams",
        label: "Teams",
        before: rosterTeamNames(base) || "Unassigned",
        after: rosterTeamNames(member) || "Unassigned",
      });
    }
    fields.forEach((field) => {
      const before = base.field_values[field.key];
      const after = member.field_values[field.key];
      if (!sameRosterValue(before, after, field)) {
        fieldChanges.push({
          key: field.key,
          label: field.label,
          field,
          before,
          after,
        });
      }
    });

    if (fieldChanges.length > 0) {
      changes.push({
        id: `updated-${identity}`,
        kind: "updated",
        name: member.display_name,
        employeeId,
        fields: fieldChanges,
      });
    }
  });

  // Baseline members no longer present in the working copy are removals.
  baseline.forEach((base, index) => {
    const employeeId = base.service_titan_employee_id;
    if (employeeId && workingStIds.has(employeeId)) return;
    changes.push({
      id: `removed-${employeeId || `row-${index}`}`,
      kind: "removed",
      name: base.display_name,
      employeeId,
      fields: [],
    });
  });

  dismissals.added.forEach((employeeId) => {
    changes.push({
      id: `dismissed-${employeeId}`,
      kind: "dismissed",
      name: dismissals.names.get(employeeId) ?? "",
      employeeId,
      fields: [],
    });
  });

  return changes;
};

// Whether an edited draft differs from its published baseline match. A member
// with no baseline (new, or an ST id not in the baseline) always counts as
// edited. Mirrors the fields diffRoster tracks: name + every roster field.
export const memberDiffersFromBaseline = (
  member: PayrollRosterMember,
  baseline: PayrollRosterMember | undefined,
  fields: PayrollRosterField[],
): boolean => {
  if (!baseline) return true;
  if (member.display_name !== baseline.display_name) return true;
  if (
    (member.user_id ?? null) !== (baseline.user_id ?? null) ||
    !sameTeamIds(member.team_access_key_ids, baseline.team_access_key_ids)
  )
    return true;
  return fields.some(
    (field) =>
      !sameRosterValue(
        baseline.field_values[field.key],
        member.field_values[field.key],
        field,
      ),
  );
};

// The draft ids that carry unpublished edits, so the read-only table can flag
// them. Existing drafts match their baseline counterpart by ST id; new drafts
// (and existing drafts whose ST id is not in the baseline) are always edited.
export const editedMemberIds = (
  drafts: readonly {
    id: string;
    isNew: boolean;
    member: PayrollRosterMember;
  }[],
  baseline: readonly PayrollRosterMember[],
  fields: PayrollRosterField[],
): Set<string> => {
  const baselineByStId = indexByStId(baseline);
  const ids = new Set<string>();

  drafts.forEach((draft) => {
    if (draft.isNew) {
      ids.add(draft.id);
      return;
    }
    const base = draft.member.service_titan_employee_id
      ? baselineByStId.get(draft.member.service_titan_employee_id)
      : undefined;
    if (memberDiffersFromBaseline(draft.member, base, fields)) {
      ids.add(draft.id);
    }
  });

  return ids;
};
